import type { Modal } from 'antd';

/** Context-aware modal API from `Modal.useModal()`, so styles reach the sidebar shadow root. */
export type DescriptionPasteLimitModalApi = ReturnType<typeof Modal.useModal>[0];

export type ShowDescriptionPasteLimitWarningArgs = {
  modal: DescriptionPasteLimitModalApi;
  canInsert: boolean;
  storageLimit?: boolean;
  onInsert?: () => void;
  onCancel?: () => void;
  /** Sidebar (and kanban) mount point, same container as the Mark as dropdown. */
  getContainer?: () => HTMLElement;
};

type ModalHandle = { destroy: () => void };

let activePasteLimitModal: ModalHandle | null = null;

/**
 * Confirms that a paste would exceed the description limit.
 * Inserts shortened text only after the user confirms; Cancel / close drops the paste.
 */
export function showDescriptionPasteLimitWarning({
  modal,
  canInsert,
  storageLimit = false,
  onInsert,
  onCancel,
  getContainer
}: ShowDescriptionPasteLimitWarningArgs): void {
  activePasteLimitModal?.destroy();
  activePasteLimitModal = null;

  const container = getContainer?.();
  const shared = {
    centered: true,
    ...(container ? { getContainer: () => container } : {})
  };

  if (!canInsert) {
    activePasteLimitModal = modal.warning({
      ...shared,
      title: 'Description is too long',
      content: storageLimit
        ? 'This description contains too much formatting or link data.'
        : 'This paste does not fit within the 2,000 character limit.',
      okText: 'OK',
      onOk: () => onCancel?.()
    });
    return;
  }

  activePasteLimitModal = modal.confirm({
    ...shared,
    title: 'Description is too long',
    content:
      storageLimit
        ? 'This paste contains too much formatting or link data. Insert the shortened text?'
        : 'This paste is longer than the 2,000 character limit. Insert the shortened text?',
    okText: 'Insert shortened text',
    cancelText: 'Cancel',
    onOk: () => onInsert?.(),
    onCancel: () => onCancel?.()
  });
}
